import { AlertTriangle, BellOff, BellRing, CalendarCheck, Check, Download, Flag, KeyRound, LogIn, MapPin, Megaphone, Settings, Trash2, Trophy, Users } from "lucide-react";

const ALL_MILESTONES = [100, 250, 500, 750, 1000];
const slug = (s) => s.replace(/\s+/g, "-").toLowerCase();

const ACTIVITY_META = {
  login: { icon: LogIn, label: "Signed in" },
  login_failed: { icon: AlertTriangle, label: "Failed sign-in attempt" },
  waitlist_delete: { icon: Trash2, label: "Removed a waitlist entry" },
  password_reset: { icon: KeyRound, label: "Password reset" },
  settings_change: { icon: Settings, label: "Changed settings" },
  user_status: { icon: Users, label: "Changed member status" },
  broadcast: { icon: Megaphone, label: "Sent a broadcast" },
  event_edit: { icon: CalendarCheck, label: "Edited an event" },
  event_review: { icon: CalendarCheck, label: "Reviewed an event" },
  report_resolve: { icon: Flag, label: "Resolved a report" },
  admin_removed: { icon: Trash2, label: "Removed an admin" },
  trial_reminder: { icon: BellRing, label: "Trial reminder emailed" },
  trial_reminder_run: { icon: BellRing, label: "Ran trial reminders" },
};

const ASSETS = [
  { label: "TikTok A — stop scrolling", href: "/tiktok-promo-a.mp4", testid: "admin-asset-tiktok-a" },
  { label: "TikTok B — apps are broken", href: "/tiktok-promo-b.mp4", testid: "admin-asset-tiktok-b" },
  { label: "TikTok C — no games ever", href: "/tiktok-promo-c.mp4", testid: "admin-asset-tiktok-c" },
  { label: "Brand film (88s, landscape)", href: "/how-it-works.mp4", testid: "admin-asset-brand-film" },
];

const Card = ({ children, testId, className = "" }) => (
  <div className={`rounded-3xl border border-cream/10 p-6 ${className}`} data-testid={testId}>{children}</div>
);

const CardTitle = ({ children }) => <p className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-cream/40">{children}</p>;

const Stat = ({ icon: Icon, value, label, testId }) => (
  <Card testId={testId}>
    <Icon className="h-5 w-5 text-gold" />
    <p className="mt-4 font-serif text-4xl tracking-tight">{value}</p>
    <p className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-cream/40">{label}</p>
  </Card>
);

export const OverviewStats = ({ total, cityStats }) => {
  const top = cityStats[0];
  const topLabel = top ? `Top city · ${top[1]} signup${top[1] === 1 ? "" : "s"}` : "Top city";
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <Stat icon={Users} value={total} label="Total signups" testId="admin-stat-total" />
      <Stat icon={MapPin} value={cityStats.length} label="Cities represented" testId="admin-stat-cities" />
      <Stat icon={Trophy} value={top?.[0] || "—"} label={topLabel} testId="admin-stat-top-city" />
    </div>
  );
};

export const CityBreakdown = ({ cityStats }) => {
  if (cityStats.length === 0) return null;
  const max = cityStats[0][1];
  return (
    <Card className="mt-8" testId="admin-city-breakdown">
      <CardTitle>Where to launch first</CardTitle>
      <div className="space-y-3">
        {cityStats.slice(0, 6).map(([city, count]) => (
          <div key={city} className="flex items-center gap-4" data-testid={`admin-city-row-${slug(city)}`}>
            <span className="w-32 truncate text-sm text-cream/70">{city}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-cream/10">
              <div className="h-full rounded-full bg-gold" style={{ width: `${Math.max(4, (count / max) * 100)}%` }} />
            </div>
            <span className="w-8 text-right text-sm font-bold text-gold">{count}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

const MilestoneChip = ({ city, ms, reached }) => (
  <span
    data-testid={`admin-milestone-chip-${slug(city)}-${ms}`}
    title={reached ? `${ms.toLocaleString()} milestone email sent` : `${ms.toLocaleString()} not reached yet`}
    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold tracking-wide ${reached ? "border-gold/50 bg-gold/15 text-gold" : "border-cream/10 text-cream/30"}`}
  >
    {reached && <Check className="h-3 w-3" />}
    {ms.toLocaleString()}
  </span>
);

export const MilestonesCard = ({ milestones }) => {
  if (milestones.length === 0) return null;
  return (
    <Card className="mt-8" testId="admin-milestones">
      <CardTitle>City milestones &amp; email opt-outs</CardTitle>
      <div className="space-y-5">
        {milestones.slice(0, 8).map((m) => (
          <div key={m.city} className="flex flex-wrap items-center gap-x-5 gap-y-3" data-testid={`admin-milestone-row-${slug(m.city)}`}>
            <div className="w-40">
              <p className="truncate text-sm font-semibold text-cream/80">{m.city}</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-xs text-cream/40" data-testid={`admin-opt-outs-${slug(m.city)}`}>
                <BellOff className="h-3 w-3" />
                {m.opt_outs} opted out · {m.count} member{m.count === 1 ? "" : "s"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {ALL_MILESTONES.map((ms) => <MilestoneChip key={ms} city={m.city} ms={ms} reached={m.milestones.includes(ms)} />)}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export const MarketingAssets = () => (
  <Card className="mt-8" testId="admin-assets">
    <p className="mb-1 text-xs font-bold uppercase tracking-[0.2em] text-cream/40">Marketing assets</p>
    <p className="text-sm text-cream/50">Ready-to-post videos. The TikTok promos come in three hook variants for A/B testing.</p>
    <div className="mt-4 flex flex-wrap gap-3">
      {ASSETS.map((a) => (
        <a key={a.href} href={a.href} download target="_blank" rel="noopener noreferrer" data-testid={a.testid} className="flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink active:scale-95">
          <Download className="h-3.5 w-3.5" />
          {a.label}
        </a>
      ))}
    </div>
  </Card>
);

const activityDate = (at) => new Date(at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const ActivityFeed = ({ activity }) => (
  <div className="mt-12" data-testid="admin-activity">
    <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-cream/40">Recent activity ({activity.length})</p>
    {activity.length === 0 ? (
      <p className="rounded-3xl border border-cream/10 px-6 py-8 text-center text-sm text-cream/40" data-testid="admin-activity-empty">
        No activity yet — sign-ins, deletions, and password resets will appear here.
      </p>
    ) : (
      <div className="overflow-hidden rounded-3xl border border-cream/10">
        {activity.map((a, i) => {
          const meta = ACTIVITY_META[a.action] || { icon: LogIn, label: a.action };
          return (
            <div key={a.id || `${a.at}-${a.action}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-cream/5 px-6 py-3.5 text-sm last:border-0" data-testid={`admin-activity-row-${i}`}>
              <meta.icon className={`h-4 w-4 shrink-0 ${a.action === "login_failed" ? "text-red-400" : "text-gold"}`} />
              <span className="font-semibold text-cream/80">{meta.label}</span>
              <span className="text-cream/50">{a.actor}</span>
              {a.detail && <span className="truncate text-cream/40">· {a.detail}</span>}
              <span className="ml-auto text-xs uppercase tracking-[0.15em] text-cream/30">{activityDate(a.at)}</span>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export const ContactMessages = ({ messages }) => (
  <div className="mt-12" data-testid="admin-messages">
    <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-cream/40">Contact messages ({messages.length})</p>
    {messages.length === 0 ? (
      <p className="rounded-3xl border border-cream/10 px-6 py-8 text-center text-sm text-cream/40" data-testid="admin-messages-empty">
        No messages yet — notes from the contact page will land here.
      </p>
    ) : (
      <div className="space-y-4">
        {messages.map((m, i) => (
          <Card key={m.id} testId={`admin-message-${i}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-serif text-lg tracking-tight">
                {m.name} <span className="text-sm text-cream/50">· {m.email}</span>
              </p>
              <p className="text-xs uppercase tracking-[0.15em] text-cream/40">
                {new Date(m.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-cream/70">{m.message}</p>
          </Card>
        ))}
      </div>
    )}
  </div>
);
